import { useMemo } from 'react'
import { isFunction } from '@pogokid/util/lodash'
import { normaliseIsoDateString } from '@pogokid/util/datetime'
import {
  useComparatorRef,
  useEffectOnce,
  useSetState,
} from '@pogokid/react-hooks'
import {
  ScheduleProviderPropsWithDefaults,
  ShowCreateEventDetailEvent,
  ShowEventDetailEvent,
} from './SchedulerProviderProps'
import {
  SchedulerState,
  SchedulerStateActions,
  SelectedRange,
} from '../types'
import { getVisibleRange } from '../helpers/dateRanges'
import { ISODateString } from '@pogokid/util/datetime'
import { getAllLogger } from '@pogokid/log'

const log = getAllLogger('ui:useScheduler')

export function useScheduler({
  selectedDate,
  view,
  week,
  day,
  onEventDrop,
  onSelectedDateChanged,
  onShowEventDetail,
  onShowCreateEventDetail,
}: ScheduleProviderPropsWithDefaults): [
  SchedulerState,
  SchedulerStateActions
] {
  const [state, setState] = useSetState<SchedulerState>({
    loading: true,
    mounted: false,
    addEventRange: undefined,
  })

  const stateActions = useMemo((): SchedulerStateActions => {
    /**
     * Used when showing (or hiding) the full details
     * of an event which opens by default in a dialog.
     */
    function showEventDetail(eventId: string) {
      const showEventDetailEvent: ShowEventDetailEvent =
        new CustomEvent('mui-scheduler__showEventDetail', {
          detail: { eventId },
          bubbles: false,
          composed: false,
          cancelable: true,
        })
      if (onShowEventDetail) {
        onShowEventDetail(showEventDetailEvent)
      }
      if (!showEventDetailEvent.defaultPrevented) {
        setState(() => ({
          addEventRange: null,
          selectedEvent: eventId,
        }))
      }
    }

    function showCreateEventDetail(range: SelectedRange): void {
      const showCreateEventDetailEvent: ShowCreateEventDetailEvent =
        new CustomEvent('mui-scheduler__showCreateEventDetail', {
          detail: { range },
          bubbles: false,
          composed: false,
          cancelable: true,
        })
      if (onShowCreateEventDetail) {
        onShowCreateEventDetail(showCreateEventDetailEvent)
      }
      setState(() => ({
        addEventRange: range,
        selectedEvent: null,
      }))
    }

    function closeDialog() {
      if (onShowCreateEventDetail) {
        const showCreateEventDetailEvent: ShowCreateEventDetailEvent =
          new CustomEvent(
            'mui-scheduler__showCreateEventDetail',
            {
              detail: { range: null },
              bubbles: false,
              composed: false,
              cancelable: true,
            }
          )
        onShowCreateEventDetail(showCreateEventDetailEvent)
      }
      if (onShowEventDetail) {
        const showEventDetailEvent: ShowEventDetailEvent =
          new CustomEvent('mui-scheduler__showEventDetail', {
            detail: { eventId: null },
            bubbles: false,
            composed: false,
            cancelable: true,
          })
        onShowEventDetail(showEventDetailEvent)
      }
      setState({
        addEventRange: null,
        selectedEvent: null,
      })
    }

    function triggerLoading(status: boolean) {
      // TODO: DT 2022-05-13 Trigger if not out-sourced by props
      if (typeof state.loading === 'undefined') {
        setState(() => ({ loading: status }))
      }
    }

    async function handleGotoDay(selectedDate: ISODateString) {
      setState(() => ({
        loading: true,
      }))
      const visibleRange = getVisibleRange(
        view,
        new Date(selectedDate),
        week?.weekStartOn,
        day?.startHour,
        day?.endHour,
        day?.plusDays
      )
      if (isFunction(onSelectedDateChanged)) {
        await onSelectedDateChanged(
          new CustomEvent('selectedDateChanged', {
            detail: {
              day: selectedDate,
              visibleRange: {
                start: visibleRange.start.toISOString(),
                end: visibleRange.end.toISOString(),
              },
            },
            bubbles: false,
            composed: false,
            cancelable: true,
          })
        )
      }
      setState(() => ({
        loading: false,
      }))
    }

    async function onDrop(
      eventId: string,
      droppedStartTime: Date
    ) {
      try {
        stateActions.triggerLoading(true)
        if (isFunction(onEventDrop)) {
          await onEventDrop(eventId, droppedStartTime)
        }
      } finally {
        stateActions.triggerLoading(false)
      }
    }

    return {
      showEventDetail,
      setAddEventRange: showCreateEventDetail,
      closeDialog,
      triggerLoading,
      handleGotoDay,
      onDrop,
    } as SchedulerStateActions
  }, [
    day?.endHour,
    day?.plusDays,
    day?.startHour,
    onEventDrop,
    onSelectedDateChanged,
    onShowCreateEventDetail,
    onShowEventDetail,
    setState,
    state.loading,
    view,
    week?.weekStartOn,
  ])

  useComparatorRef(
    selectedDate,
    (v1, v2) =>
      normaliseIsoDateString(v1) === normaliseIsoDateString(v2),
    (current) => {
      if (current) {
        log.verbose('selectedDate changed, goto', current)
        stateActions.handleGotoDay(current)
      }
    },
    true
  )

  useEffectOnce(() => {
    if (!state.mounted) {
      setState({ mounted: true })
    }
  })

  return [state, stateActions]
}
